import { useRef, type ReactNode } from "react";
import {
  Download,
  PackageCheck,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Server,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getHostLabel } from "@/lib/chat/cli-guidance";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import { useScrollSpotlight } from "../scroll-spotlight";

import { CopyableTerminal } from "../copyable-terminal";

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, description, accentClass }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string; accentClass: string }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06]">
        <Icon className={cn("h-5 w-5", accentClass)} />
      </div>
      <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1 — What is SpecTree Update?                                         */
/* -------------------------------------------------------------------------- */

export function WhatIsUpdateStep() {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Why keep SpecTree updated?">
        <p className="text-sm leading-7 text-muted-foreground">
          SpecTree agents, skills, and instructions are delivered as{" "}
          <strong className="text-foreground/90">skill packs</strong> installed in your{" "}
          <code className="rounded bg-white/[0.06] px-1.5 py-0.5 text-xs text-foreground/80">.github/</code>{" "}
          directory. New versions bring improved agent behavior, bug fixes, and new
          capabilities. The Update skill checks what you have installed and brings
          everything up to date.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="What gets updated?">
        <div className="grid gap-3 md:grid-cols-3">
          <InfoCard
            icon={PackageCheck}
            title="Skill packs"
            description="Agent definitions, skills, and instruction files in your .github/ directory. Tracked via .spectree/manifest.json."
            accentClass="text-amber-400"
          />
          <InfoCard
            icon={Server}
            title="MCP server"
            description="The SpecTree MCP server (@ttc-spectree/spectree-mcp). If you run it via npx, it auto-fetches the latest version."
            accentClass="text-amber-400"
          />
          <InfoCard
            icon={RefreshCw}
            title="Drift detection"
            description="Finds files that were manually changed, are missing, or are untracked — so you know if anything is out of sync."
            accentClass="text-amber-400"
          />
        </div>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="When to run it">
        <p className="text-sm leading-7 text-muted-foreground">
          Run the Update skill when you see an update notification in SpecTree, when
          {"you're"} troubleshooting an issue that might be fixed in a newer version, or
          periodically to stay current. {"It's"} safe to run anytime — it will tell you
          {"if"} everything is already up to date.
        </p>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 2 — Check for updates                                                */
/* -------------------------------------------------------------------------- */

export function CheckUpdatesStep() {
  const host = useResolvedHost();
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Invoke the Update skill">
        <p className="text-sm leading-7 text-muted-foreground">
          In your {getHostLabel(host)} session, invoke the skill:
        </p>
        <CopyableTerminal title={getHostLabel(host)} copyText="Invoke SpecTree-Update to check for updates">
          <p>
            <span className="text-amber-400/70">{">"} </span>
            <span className="text-foreground">Invoke SpecTree-Update to check for updates</span>
          </p>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Review the version comparison">
        <p className="text-sm leading-7 text-muted-foreground">
          The skill reads your local{" "}
          <code className="rounded bg-white/[0.06] px-1.5 py-0.5 text-xs text-foreground/80">.spectree/manifest.json</code>,
          compares it with the registry, and shows you a clear table:
        </p>
        <CopyableTerminal title={`${getHostLabel(host)} — Update check`}>
          <div className="space-y-1.5">
            <p className="text-amber-400">SpecTree Skill Pack Updates Available</p>
            <p className="text-muted-foreground/70">| Pack           | Installed | Latest | Status   |</p>
            <p className="text-muted-foreground/70">|----------------|-----------|--------|----------|</p>
            <p className="text-foreground/80">| @spectree/full | 1.2.0     | 1.3.0  | Update   |</p>
          </div>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Confirm and apply">
        <p className="text-sm leading-7 text-muted-foreground">
          If updates are available, the skill asks for your confirmation before proceeding.
          Once you say yes, it runs the SpecTree CLI update command:
        </p>
        <CopyableTerminal
          title="Terminal"
          copyText="npx --yes @ttc-spectree/spectree-cli update --registry https://spectree-api.toro.com --yes"
        >
          <p>
            <span className="text-foreground/45">$ </span>
            <span className="text-foreground">npx --yes @ttc-spectree/spectree-cli update</span>
          </p>
          <p className="text-foreground/45">{"  "}--registry https://spectree-api.toro.com --yes</p>
        </CopyableTerminal>
        <p className="mt-2 text-sm leading-7 text-muted-foreground">
          After the update completes, it reports which packs were updated, how many files
          changed, and the new version numbers.
        </p>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 3 — Troubleshooting                                                  */
/* -------------------------------------------------------------------------- */

export function UpdateTroubleshootingStep() {
  const host = useResolvedHost();
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Manifest missing?">
        <p className="text-sm leading-7 text-muted-foreground">
          If you see {"\"manifest not found\""}, the skill packs {"haven't"} been installed yet.
          Run the install command first:
        </p>
        <CopyableTerminal
          title="Terminal"
          copyText="npx --yes @ttc-spectree/spectree-cli install @spectree/full --registry https://spectree-api.toro.com"
        >
          <p>
            <span className="text-foreground/45">$ </span>
            <span className="text-foreground">npx --yes @ttc-spectree/spectree-cli install @spectree/full</span>
          </p>
          <p className="text-foreground/45">{"  "}--registry https://spectree-api.toro.com</p>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Network errors">
        <p className="text-sm leading-7 text-muted-foreground">
          If the registry check fails, make sure you have internet access and are not
          behind a restrictive proxy. Check your npm proxy settings:
        </p>
        <CopyableTerminal title="Terminal" copyText="npm config get proxy">
          <p>
            <span className="text-foreground/45">$ </span>
            <span className="text-foreground">npm config get proxy</span>
          </p>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Check for drift">
        <p className="text-sm leading-7 text-muted-foreground">
          If things are behaving oddly, you can check for discrepancies between your
          local files and the manifest. The Update skill runs drift detection
          automatically, but you can also ask for it explicitly:
        </p>
        <CopyableTerminal title={getHostLabel(host)} copyText="Check for SpecTree skill pack drift">
          <p>
            <span className="text-amber-400/70">{">"} </span>
            <span className="text-foreground">Check for SpecTree skill pack drift</span>
          </p>
        </CopyableTerminal>
        <p className="mt-2 text-sm leading-7 text-muted-foreground">
          This reports untracked files, missing files, and version mismatches — helping
          you identify if something was manually changed or accidentally deleted.
        </p>
        <div className="grid gap-3 md:grid-cols-3">
          <InfoCard
            icon={AlertTriangle}
            title="Untracked files"
            description="Files on disk that aren't tracked by any installed skill pack. May be leftovers from a manual edit."
            accentClass="text-amber-400"
          />
          <InfoCard
            icon={Download}
            title="Missing files"
            description="Files that should exist according to the manifest but are missing from disk. Reinstall will restore them."
            accentClass="text-amber-400"
          />
          <InfoCard
            icon={CheckCircle2}
            title="Version match"
            description="When everything is in sync, the skill confirms all packs are current and no drift was detected."
            accentClass="text-amber-400"
          />
        </div>
      </NumberedInstructionLast>
    </div>
  );
}
